import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'utils/api/axios'
import AxiosUtils from 'utils/api/axiosUtils'
import { Message, MessageSeverity } from 'constants/message'
import WP_Constants from '../wpConstants'
import { HTTP_CODES } from 'constants/constants'
import './widgetSummary.css'
import { Button, Table, TableBody, TableHead, TableRow, Typography } from '@airbus/components-react/components'
import LangContext from 'context/LangContext'
import MessageContext from 'context/MessageContext'
import LoadingContext from 'context/LoadingContext'
import UrlApis from 'utils/api/urlApis'
import AuthContext from 'context/AuthContext'
import ResponsiveTableCell from 'components/responsiveTableCell'

const WidgetSummary = ({ bdvnumdoc, summary, cellName, handleSummaryChange }) => {
  const { t } = useTranslation()

  const [data, setData] = useState()
  const [isLoadInProgressActive, setIsLoadInProgressActive] = useState(false)

  const { lang } = useContext(LangContext)
  const { handleDisplayMessage } = useContext(MessageContext)
  const { handleLoadInProgress } = useContext(LoadingContext)
  const { handleRequestLogin } = useContext(AuthContext)

  /********
   * Synchronization of widget's data to keep track of them
   ********/

  const loadData = async () => {
    handleLoadInProgress(true)
    setIsLoadInProgressActive(true)

    try {
      console.debug(
        'Widget <' +
          t('9500018') +
          '> :: called URL = ' +
          encodeURI(UrlApis.URL_WPSTATUS_VISIT_SUMMARY_GET + '?lang=' + lang + '&bdvnumdoc=' + bdvnumdoc),
      )
      const headerCompleted = await AxiosUtils.getHeader(AxiosUtils.HEADERS.HEADER_READ)
      const response = await axios.get(
        encodeURI(UrlApis.URL_WPSTATUS_VISIT_SUMMARY_GET + '?lang=' + lang + '&bdvnumdoc=' + bdvnumdoc),
        {},
        {
          headers: headerCompleted,
          withCredentials: false,
        },
      )

      // Init widget locally
      console.debug('data retrieved <' + t('9500018') + '>')
      setData(response?.data)
    } catch (err) {
      console.debug('ERR <' + t('9500018') + '>: ' + err)
      if (err.response.status === HTTP_CODES.HTTP_CODE_SESSION_EXPIRED) {
        handleDisplayMessage(new Message(MessageSeverity.WARNING, t('9500018'), null, t('sessionExpired')))
        handleRequestLogin()
      } else if (err.response.status === HTTP_CODES.HTTP_CODE_NOT_FOUND) {
        handleDisplayMessage(new Message(MessageSeverity.ERROR, t('9500018'), null, t('9500173')))
      } else {
        handleDisplayMessage(
          new Message(
            MessageSeverity.ERROR,
            t('9500018'),
            err.response.data?.amasisCode,
            err.response.data?.description
              ? err.response.data?.description
              : err.response.data.title
                ? err.response.data.title
                : err.response.data.message
                  ? err.response.data.message
                  : err.response.data.error,
          ),
        )
      }
    } finally {
      handleLoadInProgress(false)
      setIsLoadInProgressActive(false)
    }
  }

  // Sync on receiving data from server (axios)
  useEffect(() => {
    // Clear user's selection which will be notified to the page

    if (bdvnumdoc !== undefined) {
      loadData()
    }
  }, [bdvnumdoc])

  const handleSelectionSummary = (e) => {
    handleSummaryChange(e.currentTarget.id, e.currentTarget.name)
    // Reload summary component
    loadData()
  }

  return (
    <div className='WidgetWpSummary'>
      <Typography variant='large' style={{ marginBottom: '20px' }}>
        {t('9500018')}
      </Typography>
      {data && JSON.stringify(data) !== '{}' && JSON.stringify(data) !== '[]' ? (
        <Table>
          <colgroup>
            <col style={{ width: '20%', overflow: 'hidden' }} />
            <col style={{ width: '15%', overflow: 'hidden' }} />
            <col style={{ width: '15%', overflow: 'hidden' }} />
            <col style={{ width: '15%', overflow: 'hidden' }} />
            <col style={{ width: '15%', overflow: 'hidden' }} />
            <col style={{ width: '15%', overflow: 'hidden' }} />
            <col style={{ width: '15%', overflow: 'hidden' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <ResponsiveTableCell>
                <Typography style={{ fontWeight: 'font.fontWeightBold' }}>{t('7003008')}</Typography>
              </ResponsiveTableCell>
              <ResponsiveTableCell>{t('9500117')}</ResponsiveTableCell>
              <ResponsiveTableCell>{t('9400199')}</ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>{t('0LMA106')}</ResponsiveTableCell>
              <ResponsiveTableCell>{t('9500111')}</ResponsiveTableCell>
              <ResponsiveTableCell>{t('9100848')}</ResponsiveTableCell>
              <ResponsiveTableCell>{t('9500118')}</ResponsiveTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow key={'line_wpstatus_summary_op'}>
              <ResponsiveTableCell className='emphasized'>{t('9500097')}</ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  id={WP_Constants.OPERATIONS_OPENED}
                  name={WP_Constants.OPERATIONS_OPENED}
                  variant='secondary'
                  className={cellName === WP_Constants.OPERATIONS_OPENED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOpOpn}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_CLOSED}
                  name={WP_Constants.OPERATIONS_CLOSED}
                  className={cellName === WP_Constants.OPERATIONS_CLOSED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOpClo}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS}
                  name={WP_Constants.OPERATIONS}
                  className={cellName === WP_Constants.OPERATIONS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOpTotal}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_DEFERRED}
                  name={WP_Constants.OPERATIONS_DEFERRED}
                  className={cellName === WP_Constants.OPERATIONS_DEFERRED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOpDfr}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_WITHTS}
                  name={WP_Constants.OPERATIONS_WITHTS}
                  className={cellName === WP_Constants.OPERATIONS_WITHTS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOpWithTs}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_TS}
                  name={WP_Constants.OPERATIONS_TS}
                  className={cellName === WP_Constants.OPERATIONS_TS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOpTs}
                </Button>
              </ResponsiveTableCell>
            </TableRow>
            <TableRow key={'line_wpstatus_summary_noop'}>
              <ResponsiveTableCell>
                <dd>{t('9500119')}</dd>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_NOOP_OPENED}
                  name={WP_Constants.OPERATIONS_NOOP_OPENED}
                  className={cellName === WP_Constants.OPERATIONS_NOOP_OPENED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbNoopOpn}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_NOOP_CLOSED}
                  name={WP_Constants.OPERATIONS_NOOP_CLOSED}
                  className={cellName === WP_Constants.OPERATIONS_NOOP_CLOSED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbNoopClo}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_NOOP}
                  name={WP_Constants.OPERATIONS_NOOP}
                  className={cellName === WP_Constants.OPERATIONS_NOOP ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbNoopTotal}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_NOOP_DEFERRED}
                  name={WP_Constants.OPERATIONS_NOOP_DEFERRED}
                  className={cellName === WP_Constants.OPERATIONS_NOOP_DEFERRED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbNoopDfr}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_NOOP_WITHTS}
                  name={WP_Constants.OPERATIONS_NOOP_WITHTS}
                  className={cellName === WP_Constants.OPERATIONS_NOOP_WITHTS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbNoopWithTs}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_NOOP_TS}
                  name={WP_Constants.OPERATIONS_NOOP_TS}
                  className={cellName === WP_Constants.OPERATIONS_NOOP_TS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbNoopTs}
                </Button>
              </ResponsiveTableCell>
            </TableRow>
            <TableRow key={'line_wpstatus_summary_oop'}>
              <ResponsiveTableCell>
                <dd>{t('9500098')}</dd>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_OOP_OPENED}
                  name={WP_Constants.OPERATIONS_OOP_OPENED}
                  className={cellName === WP_Constants.OPERATIONS_OOP_OPENED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOopOpn}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_OOP_CLOSED}
                  name={WP_Constants.OPERATIONS_OOP_CLOSED}
                  className={cellName === WP_Constants.OPERATIONS_OOP_CLOSED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOopClo}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_OOP}
                  name={WP_Constants.OPERATIONS_OOP}
                  className={cellName === WP_Constants.OPERATIONS_OOP ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOopTotal}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_OOP_DEFERRED}
                  name={WP_Constants.OPERATIONS_OOP_DEFERRED}
                  className={cellName === WP_Constants.OPERATIONS_OOP_DEFERRED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOopDfr}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_OOP_WITHTS}
                  name={WP_Constants.OPERATIONS_OOP_WITHTS}
                  className={cellName === WP_Constants.OPERATIONS_OOP_WITHTS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOopWithTs}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.OPERATIONS_OOP_TS}
                  name={WP_Constants.OPERATIONS_OOP_TS}
                  className={cellName === WP_Constants.OPERATIONS_OOP_TS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOopTs}
                </Button>
              </ResponsiveTableCell>
            </TableRow>
            <TableRow key={'line_wpstatus_summary_oe'}>
              <ResponsiveTableCell className='emphasized'>{t('9500099')}</ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_OPENED}
                  name={WP_Constants.WORKORDERS_OPENED}
                  className={
                    (summary === WP_Constants.WORKORDERS_OPENED ? 'selected ' : '') +
                    'table_data_align_digital emphasized'
                  }
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeAllOpn}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_CLOSED}
                  name={WP_Constants.WORKORDERS_CLOSED}
                  className={
                    (summary === WP_Constants.WORKORDERS_CLOSED ? 'selected ' : '') +
                    'table_data_align_digital emphasized'
                  }
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeAllClo}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS}
                  name={WP_Constants.WORKORDERS}
                  className={cellName === WP_Constants.WORKORDERS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeAllTotal}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_DEFERRED}
                  name={WP_Constants.WORKORDERS_DEFERRED}
                  className={cellName === WP_Constants.WORKORDERS_DEFERRED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeAllDfr}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_WITHTS}
                  name={WP_Constants.WORKORDERS_WITHTS}
                  className={cellName === WP_Constants.WORKORDERS_WITHTS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeAllWithTs}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_TS}
                  name={WP_Constants.WORKORDERS_TS}
                  className={cellName === WP_Constants.WORKORDERS_TS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeAllTs}
                </Button>
              </ResponsiveTableCell>
            </TableRow>
            <TableRow key={'line_wpstatus_summary_oetl'}>
              <ResponsiveTableCell>
                <dd>{t('9500100')}</dd>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_TL_OPENED}
                  name={WP_Constants.WORKORDERS_TL_OPENED}
                  className={cellName === WP_Constants.WORKORDERS_TL_OPENED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeTlOpn}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_TL_CLOSED}
                  name={WP_Constants.WORKORDERS_TL_CLOSED}
                  className={cellName === WP_Constants.WORKORDERS_TL_CLOSED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeTlClo}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_TL}
                  name={WP_Constants.WORKORDERS_TL}
                  className={cellName === WP_Constants.WORKORDERS_TL ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeTlTotal}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_TL_DEFERRED}
                  name={WP_Constants.WORKORDERS_TL_DEFERRED}
                  className={cellName === WP_Constants.WORKORDERS_TL_DEFERRED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeTlDfr}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_TL_WITHTS}
                  name={WP_Constants.WORKORDERS_TL_WITHTS}
                  className={cellName === WP_Constants.WORKORDERS_TL_WITHTS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeTlWithTs}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_TL_TS}
                  name={WP_Constants.WORKORDERS_TL_TS}
                  className={cellName === WP_Constants.WORKORDERS_TL_TS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeTlTs}
                </Button>
              </ResponsiveTableCell>
            </TableRow>
            <TableRow key={'line_wpstatus_summary_oemc'}>
              <ResponsiveTableCell>
                <dd>{t('9500101')}</dd>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_MC_OPENED}
                  name={WP_Constants.WORKORDERS_MC_OPENED}
                  className={cellName === WP_Constants.WORKORDERS_MC_OPENED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeMcOpn}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_MC_CLOSED}
                  name={WP_Constants.WORKORDERS_MC_CLOSED}
                  className={cellName === WP_Constants.WORKORDERS_MC_CLOSED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeMcClo}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_MC}
                  name={WP_Constants.WORKORDERS_MC}
                  className={cellName === WP_Constants.WORKORDERS_MC ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeMcTotal}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_MC_DEFERRED}
                  name={WP_Constants.WORKORDERS_MC_DEFERRED}
                  className={cellName === WP_Constants.WORKORDERS_MC_DEFERRED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeMcDfr}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_MC_WITHTS}
                  name={WP_Constants.WORKORDERS_MC_WITHTS}
                  className={cellName === WP_Constants.WORKORDERS_MC_WITHTS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeMcWithTs}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_MC_TS}
                  name={WP_Constants.WORKORDERS_MC_TS}
                  className={cellName === WP_Constants.WORKORDERS_MC_TS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeMcTs}
                </Button>
              </ResponsiveTableCell>
            </TableRow>
            <TableRow key={'line_wpstatus_summary_oedoc'}>
              <ResponsiveTableCell>
                <dd>{t('9500102')}</dd>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_DOC_OPENED}
                  name={WP_Constants.WORKORDERS_DOC_OPENED}
                  className={cellName === WP_Constants.WORKORDERS_DOC_OPENED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeDocOpn}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_DOC_CLOSED}
                  name={WP_Constants.WORKORDERS_DOC_CLOSED}
                  className={cellName === WP_Constants.WORKORDERS_DOC_CLOSED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeDocClo}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_DOC}
                  name={WP_Constants.WORKORDERS_DOC}
                  className={cellName === WP_Constants.WORKORDERS_DOC ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeDocTotal}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_DOC_DEFERRED}
                  name={WP_Constants.WORKORDERS_DOC_DEFERRED}
                  className={cellName === WP_Constants.WORKORDERS_DOC_DEFERRED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeDocDfr}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_DOC_WITHTS}
                  name={WP_Constants.WORKORDERS_DOC_WITHTS}
                  className={cellName === WP_Constants.WORKORDERS_DOC_WITHTS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeDocWithTs}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_DOC_TS}
                  name={WP_Constants.WORKORDERS_DOC_TS}
                  className={cellName === WP_Constants.WORKORDERS_DOC_TS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeDocTs}
                </Button>
              </ResponsiveTableCell>
            </TableRow>
            <TableRow key={'line_wpstatus_summary_oekx'}>
              <ResponsiveTableCell>
                <dd>{t('9500103')}</dd>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_KX_OPENED}
                  name={WP_Constants.WORKORDERS_KX_OPENED}
                  className={cellName === WP_Constants.WORKORDERS_KX_OPENED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeKxOpn}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_KX_CLOSED}
                  name={WP_Constants.WORKORDERS_KX_CLOSED}
                  className={cellName === WP_Constants.WORKORDERS_KX_CLOSED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeKxClo}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_KX}
                  name={WP_Constants.WORKORDERS_KX}
                  className={cellName === WP_Constants.WORKORDERS_KX ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeKxTotal}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_KX_DEFERRED}
                  name={WP_Constants.WORKORDERS_KX_DEFERRED}
                  className={cellName === WP_Constants.WORKORDERS_KX_DEFERRED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeKxDfr}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_KX_WITHTS}
                  name={WP_Constants.WORKORDERS_KX_WITHTS}
                  className={cellName === WP_Constants.WORKORDERS_KX_WITHTS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeKxWithTs}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_KX_TS}
                  name={WP_Constants.WORKORDERS_KX_TS}
                  className={cellName === WP_Constants.WORKORDERS_KX_TS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeKxTs}
                </Button>
              </ResponsiveTableCell>
            </TableRow>
            <TableRow key={'line_wpstatus_summary_oemis'}>
              <ResponsiveTableCell>
                <dd>{t('9500104')}</dd>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_MIS_OPENED}
                  name={WP_Constants.WORKORDERS_MIS_OPENED}
                  className={cellName === WP_Constants.WORKORDERS_MIS_OPENED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeMisOpn}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_MIS_CLOSED}
                  name={WP_Constants.WORKORDERS_MIS_CLOSED}
                  className={cellName === WP_Constants.WORKORDERS_MIS_CLOSED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeMisClo}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_MIS}
                  name={WP_Constants.WORKORDERS_MIS}
                  className={cellName === WP_Constants.WORKORDERS_MIS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeMisTotal}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_MIS_DEFERRED}
                  name={WP_Constants.WORKORDERS_MIS_DEFERRED}
                  className={cellName === WP_Constants.WORKORDERS_MIS_DEFERRED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeMisDfr}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_MIS_WITHTS}
                  name={WP_Constants.WORKORDERS_MIS_WITHTS}
                  className={cellName === WP_Constants.WORKORDERS_MIS_WITHTS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeMisWithTs}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <Button
                  variant='secondary'
                  id={WP_Constants.WORKORDERS_MIS_TS}
                  name={WP_Constants.WORKORDERS_MIS_TS}
                  className={cellName === WP_Constants.WORKORDERS_MIS_TS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeMisTs}
                </Button>
              </ResponsiveTableCell>
            </TableRow>
            <TableRow key={'line_wpstatus_summary_ts'}>
              <ResponsiveTableCell className='emphasized'>{t('9500105')}</ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.ADDWRK_OPENED}
                  name={WP_Constants.ADDWRK_OPENED}
                  className={
                    (summary === WP_Constants.ADDWRK_OPENED ? 'selected ' : '') + 'table_data_align_digital italic'
                  }
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeTsOpn}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.ADDWRK_CLOSED}
                  name={WP_Constants.ADDWRK_CLOSED}
                  className={
                    (summary === WP_Constants.ADDWRK_CLOSED ? 'selected ' : '') + 'table_data_align_digital italic'
                  }
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeTsClo}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.TOTAL_TS}
                  name={WP_Constants.TOTAL_TS + '_1'}
                  className={cellName === WP_Constants.TOTAL_TS + '_1' ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeTsTotal}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.ADDWRK_DEFERRED}
                  name={WP_Constants.ADDWRK_DEFERRED}
                  className={cellName === WP_Constants.ADDWRK_DEFERRED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeTsDfr}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.ADDWRK_WITHTS}
                  name={WP_Constants.ADDWRK_WITHTS}
                  className={cellName === WP_Constants.ADDWRK_WITHTS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeTsWithTs}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='emphasized'>
                <Button
                  variant='secondary'
                  id={WP_Constants.ADDWRK_FROMTS}
                  name={WP_Constants.ADDWRK_FROMTS}
                  className={cellName === WP_Constants.ADDWRK_FROMTS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbOeTsTs}
                </Button>
              </ResponsiveTableCell>
            </TableRow>
            <TableRow key={'line_wpstatus_summary_optotals'} className='lineTotals'>
              <ResponsiveTableCell className='grandTotal'>{t('0LMA106')}</ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.TOTAL_OPENED}
                  name={WP_Constants.TOTAL_OPENED}
                  className={cellName === WP_Constants.TOTAL_OPENED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbTskOpn}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.TOTAL_CLOSED}
                  name={WP_Constants.TOTAL_CLOSED}
                  className={cellName === WP_Constants.TOTAL_CLOSED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbTskClo}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.TOTAL}
                  name={WP_Constants.TOTAL}
                  className={cellName === WP_Constants.TOTAL ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbTskTotal}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.TOTAL_DEFERRED}
                  name={WP_Constants.TOTAL_DEFERRED}
                  className={cellName === WP_Constants.TOTAL_DEFERRED ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbTskDfr}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.TOTAL_WITHTS}
                  name={WP_Constants.TOTAL_WITHTS}
                  className={cellName === WP_Constants.TOTAL_WITHTS ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbTskWithTs}
                </Button>
              </ResponsiveTableCell>
              <ResponsiveTableCell className='grandTotal'>
                <Button
                  variant='secondary'
                  id={WP_Constants.TOTAL_TS}
                  name={WP_Constants.TOTAL_TS + '_2'}
                  className={cellName === WP_Constants.TOTAL_TS + '_2' ? 'selected ' : ''}
                  onClick={handleSelectionSummary}
                >
                  {data.nbTskTs}
                </Button>
              </ResponsiveTableCell>
            </TableRow>
          </TableBody>
        </Table>
      ) : (
        <div style={{ paddingLeft: '1em' }}>{isLoadInProgressActive ? t('9500166') : t('9500055')}</div>
      )}
    </div>
  )
}

export default WidgetSummary
